// Feature flags mínimos (P1). Persistencia efímera (memoria) hasta añadir storage/remote.
type FlagKey =
  | 'syncDiagnostics'
  | 'experimentalRuntimeCache'
  | 'aiAssistantV2'
  | 'ui.leftOutlinePanel.v1'
  | 'ui.leftOutlinePanel.accordion.v1'
  | 'ui.progressNav.v1'
  | 'ui.outline.singleActionBar.v1'
  | 'debug.outlineTools.v1';

// Storage key para overrides persistentes en localStorage.
const STORAGE_KEY = 'wrlens:flags:v1';

// Valores por defecto (base). No mutar directamente; usar setFlag.
const defaultFlags: Record<FlagKey, boolean> = {
  syncDiagnostics: false,
  experimentalRuntimeCache: false,
  aiAssistantV2: false,
  'ui.leftOutlinePanel.v1': true,
  // Activado por defecto tras estabilizar tests (21-09-2025)
  'ui.leftOutlinePanel.accordion.v1': true,
  'ui.progressNav.v1': true,
  // Activado tras validar tests y QA inicial (21-09-2025)
  'ui.outline.singleActionBar.v1': true,
  'debug.outlineTools.v1': false,
};

// Cargar overrides persistidos si existen (solo navegador).
let persisted: Partial<Record<FlagKey, boolean>> = {};
if (typeof window !== 'undefined') {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        persisted = parsed;
      }
    }
  } catch {
    // Ignorar errores de parseo / acceso
  }
}

// Aplicar overrides vía query param (?flags=flag1:on,flag2:off) – útil para toggles ad‑hoc.
if (typeof window !== 'undefined' && window.location?.search) {
  const params = new URLSearchParams(window.location.search);
  const qp = params.get('flags');
  if (qp) {
    qp.split(',').forEach(pair => {
      const [k, v] = pair.split(':');
      if ((k as FlagKey) && v && (k in defaultFlags)) {
        persisted[k as FlagKey] = v === 'on';
      }
    });
  }
}

// Estado runtime real de flags.
const flags: Record<FlagKey, boolean> = { ...defaultFlags, ...persisted };

function persist() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
  } catch {}
}

export function isFlagEnabled(key: FlagKey): boolean {
  return !!flags[key];
}

export function setFlag(key: FlagKey, value: boolean) {
  flags[key] = value;
  persist();
  try { window.dispatchEvent(new CustomEvent('wrlens:flag-changed', { detail: { key, value } })); } catch {}
}

export function listFlags() {
  return { ...flags };
}

// Exponer interfaz de depuración (solo en desarrollo / si se define explícitamente).
declare global {
  interface Window {
    wrlensFlags?: {
      get: () => Record<FlagKey, boolean>;
      set: (k: FlagKey, v: boolean) => void;
      reset: () => void;
    };
  }
}

if (typeof window !== 'undefined' && !window.wrlensFlags) {
  window.wrlensFlags = {
    get: listFlags,
    set: (k, v) => setFlag(k, v),
    reset: () => {
      (Object.keys(defaultFlags) as FlagKey[]).forEach(k => { flags[k] = defaultFlags[k]; });
      persist();
      try { window.dispatchEvent(new CustomEvent('wrlens:flags:reset')); } catch {}
    }
  };
}
